/**
 * Department dropdown for the group form. Accepts an optional quick-add
 * callback that the parent wires up to `POST /admin/departments`.
 */

import React from 'react';
import Select, { SelectOption } from '@amzn/meridian/select';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Input from '@amzn/meridian/input';
import { DepartmentRecord } from '../service/adminApiService';

export interface DepartmentDropdownProps {
  value: string;
  onChange: (next: string) => void;
  departments: DepartmentRecord[];
  disabled?: boolean;
  onQuickAdd?: (name: string) => void | Promise<void>;
}

export const DepartmentDropdown: React.FC<DepartmentDropdownProps> = ({
  value,
  onChange,
  departments,
  disabled,
  onQuickAdd,
}) => {
  const [mode, setMode] = React.useState<'pick' | 'new'>('pick');
  const [draft, setDraft] = React.useState('');

  if (mode === 'new' && onQuickAdd) {
    return (
      <Row spacing="small" alignmentVertical="center">
        <div style={{ flex: 1 }}>
          <Input
            type="text"
            value={draft}
            onChange={setDraft}
            placeholder="New department name"
            aria-label="New department name"
          />
        </div>
        <Button
          type="primary"
          onClick={async () => {
            const name = draft.trim();
            if (!name) return;
            try {
              await onQuickAdd(name);
            } catch {
              // The caller (``GroupFormPage``) surfaces the error via a
              // toast. Bail out here so we don't set the form's
              // department value to a name that the backend rejected.
              return;
            }
            onChange(name);
            setDraft('');
            setMode('pick');
          }}
          disabled={!draft.trim()}
        >
          Create
        </Button>
        <Button type="tertiary" onClick={() => setMode('pick')}>
          Cancel
        </Button>
      </Row>
    );
  }

  return (
    <Row spacing="small" alignmentVertical="center">
      <div style={{ flex: 1 }}>
        <Select
          value={value}
          onChange={(next: string) => onChange(next)}
          placeholder="Select a department"
          disabled={disabled}
          aria-label="Department"
        >
          {departments.map((d) => (
            <SelectOption key={d.department_name} value={d.department_name} label={d.display_name || d.department_name} />
          ))}
        </Select>
      </div>
      {onQuickAdd && (
        <Button type="tertiary" onClick={() => setMode('new')} disabled={disabled}>
          + New
        </Button>
      )}
    </Row>
  );
};
